<template>
  <section
    class="w-100"
    data-cy="application-form-object-view"
    :data-view-ready="!loading">
    <!-- Loading -->
    <div
      v-if="loading"
      class="row">
      <div class="col text-center">
        <coozzy-spinner class="m-3" />
      </div>
    </div>
    <div
      v-else
      class="row m-0">
      <div
        v-if="galleryImages && galleryImages.length > 0"
        class="image-container rounded-left col-12 col-md-4"
        :style="{ 'background-image': 'url(' + galleryImages[0].url + ')' }"
        @click="openGallery" />
      <div
        v-else
        class="image-container rounded-left col-12 col-md-4"
        :style="{ 'background-image': 'url(/img/image-placeholder.png)' }" />
      <!-- Object data -->
      <div
        class="col-12 col-md-8 bg-grey rounded pt-3 mt-3 mt-md-0"
        :class="requestId ? 'cursor-pointer' : ''"
        @click="requestClick(requestId)">
        <div class="row">
          <div class="col-12 mb-3">
            <div class="marketing-account-brand">
              <img
                v-if="marketingAccount && marketingAccount.logoUrl"
                :src="marketingAccount.logoUrl"
                alt=""
                class="marketing-account-logo">
              <div
                v-if="marketingAccount?.company?.name"
                class="font-weight-bold"
                data-cy="application-form-object-account-name">
                {{ marketingAccount.company.name }}
              </div>
            </div>
          </div>
          <div
            class="col-12 mb-3 font-weight-bold"
            data-cy="application-form-object-title">
            {{ object.title }},
            <address-text
              data-cy="application-form-object-address"
              :address="object.address"
              :one-line="true" />
          </div>
          <div class="col-12 col-md-6 mb-3">
            <div class="row">
              <div class="col-6 font-weight-bold">
                {{ $t('message.tableColumnHeadings.netRent') }}
              </div>
              <div
                class="col-6"
                data-cy="application-form-object-net-rent-value">
                <template v-if="object.coldRent">
                  {{ $options.filters.formatPrice(object.coldRent, 'CHF') }}
                </template>
                <template v-else>
                  -
                </template>
              </div>
            </div>
            <div class="row">
              <div class="col-6 font-weight-bold">
                {{ $t('message.tableColumnHeadings.utilities_HC') }}
              </div>
              <div
                class="col-6"
                data-cy="application-form-object-utilities-value">
                <template v-if="sumAdditionalAndHeating(object.additional, object.heating) !== '-'">
                  {{ $options.filters.formatPrice(sumAdditionalAndHeating(object.additional, object.heating), 'CHF') }}
                </template>
                <template v-else>
                  -
                </template>
              </div>
            </div>
            <div class="row">
              <div class="col-6 font-weight-bold">
                {{ $t('message.tableColumnHeadings.grossRent') }}
              </div>
              <div
                class="col-6"
                data-cy="application-form-object-gross-rent-value">
                <template v-if="grossRent">
                  {{ $options.filters.formatPrice(grossRent, 'CHF') }}
                </template>
                <template v-else>
                  -
                </template>
              </div>
            </div>
            <div class="row">
              <div class="col-6 font-weight-bold">
                {{ $t('message.onBoarding.buildings.objects.rooms') }}
              </div>
              <div
                class="col-6"
                data-cy="application-form-object-rooms-value">
                {{ $options.filters.displayOptionalValue(object.rooms) }}
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6 mb-3">
            <div class="row">
              <div class="col-6 font-weight-bold">
                {{ $t('message.onBoarding.buildings.objects.floor') }}
              </div>
              <div
                class="col-6"
                data-cy="application-form-object-floor-value">
                {{ formatFloor(object.floor) }}
              </div>
            </div>
            <div class="row">
              <div class="col-6 font-weight-bold">
                {{ $t('message.tableColumnHeadings.reference') }}
              </div>
              <div
                class="col-6"
                data-cy="application-form-object-reference-value">
                {{ object.publicId }}
              </div>
            </div>
            <div class="row">
              <div class="col-6 font-weight-bold">
                {{ $t('message.tableColumnHeadings.availability') }}
              </div>
              <div
                class="col-6"
                data-cy="application-form-object-availability-value">
                <template v-if="object.availability">
                  <template v-if="object.availability.type === 'IMMEDIATELY'">
                    {{ $t('message.onBoarding.buildings.objects.availability.IMMEDIATELY') }}
                  </template>
                  <template v-else-if="object.availability.type === 'BY_ARRANGEMENT'">
                    {{ $t('message.onBoarding.buildings.objects.availability.BY_ARRANGEMENT') }}
                  </template>
                  <template v-else-if="object.availability.type === 'FROM_DATE'">
                    {{ $options.filters.timestampToDate(object.availability.availableFrom) }}
                  </template>
                  <template v-else-if="object.availability.type === 'TEMPORARY_LIVING'">
                    {{ $options.filters.timestampToDate(object.availability.availableFrom) }}
                  </template>
                </template>
                <template v-else>
                  -
                </template>
              </div>
            </div>
            <div
              v-if="object.category !== 'HOUSE' && object.category !== 'APARTMENT'"
              class="row">
              <div class="col-6 font-weight-bold">
                {{ $t('message.onBoarding.buildings.objects.effectiveArea') }}
              </div>
              <div
                class="col-6"
                data-cy="application-form-object-effective-area-value">
                {{ $options.filters.formatArea(object.effectiveArea) }}
              </div>
            </div>
            <div
              v-else
              class="row">
              <div class="col-6 font-weight-bold">
                {{ $t('message.onBoarding.buildings.objects.livingSpace') }}
              </div>
              <div
                class="col-6"
                data-cy="application-form-object-living-space-value">
                {{ $options.filters.formatArea(object.livingArea) }}
              </div>
            </div>
          </div>
          <div class="col-12 mb-3">
            <div
              class="row">
              <template>
                <div class="col-4 offset-md-4">
                  <coozzy-button
                    v-if="accountId && $route.name !== 'UserRequestDetailView'"
                    class="w-100"
                    design="prop-green"
                    @click.stop="requestClick(requestId)">
                    {{ $t('message.generic.form.toCommunication') }}
                  </coozzy-button>
                </div>
              </template>
              <template v-if="showApplyButton && accountId">
                <div class="col-4">
                  <coozzy-button
                    class="w-100"
                    design="prop-green"
                    @click.stop="applyClicked">
                    {{ $t('message.generic.form.apply') }}
                  </coozzy-button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import ObjectApi from '@/misc/apis/ObjectApi'
import RequestApi from '@/marketingBasic/api/RequestApi'
import MediaApi from '@/misc/apis/MediaApi'
import UserApi from '@/misc/apis/UserApi'
import AddressText from '@/framework/components/misc/AddressText.vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import { formatting } from '@/mixins/formatting'
import { user } from '@/mixins/user'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import {
  ApplicationFormMarketingAccountDisplay,
  ApplicationFormMarketingObjectViewModel
} from '@/types/models'

interface GalleryImageItem {
  url: string
  mediaId: string
  order: number
}

interface SentRequestListItem {
  id: string | number
  objectId: string
}

interface MediaByIdPayload {
  media: { id: string; url: string }
}

interface ObjectViewData {
  objectId: string
  object: ApplicationFormMarketingObjectViewModel | null
  request: SentRequestListItem | null
  loading: boolean
  galleryImages: GalleryImageItem[]
  marketingAccount: ApplicationFormMarketingAccountDisplay | null
  requestList: SentRequestListItem[]
}

export default Vue.extend({
  name: 'ApplicationFormObjectView',
  components: {
    CoozzyButton,
    CoozzySpinner,
    AddressText
  },
  mixins: [user, formatting],
  props: {
    requestId: {
      type: [String, Number] as PropType<string | number>,
      default: ''
    },
    defaultObjectId: {
      type: [String, Number] as PropType<string | number>,
      default: ''
    },
    showApplyButton: {
      type: Boolean,
      default: true
    }
  },
  data(): ObjectViewData {
    return {
      objectId: '',
      object: null,
      request: null,
      loading: true,
      galleryImages: [],
      marketingAccount: null,
      requestList: []
    }
  },
  computed: {
    isPublicViewingAppointmentsPage(): boolean {
      return this.$route.name === 'SelectViewingAppointmentsPage' ||
        this.$route.name === 'AddViewingAppointmentsPage'
    },
    grossRent(): number | null {
      if (!this.object) {
        return null
      }
      const property = this.object
      const total =
        (property.heating !== -1 ? property.heating : 0) +
        (property.additional !== -1 ? property.additional : 0) +
        (property.coldRent !== -1 ? property.coldRent : 0)

      return total !== 0 ? total : null
    }
  },
  mounted() {
    if (this.requestId) {
      RequestApi.getListSent(this.$store.getters['user/getAccountId'] as string)
        .then(response => {
          this.requestList = response.requests as SentRequestListItem[]
            this.request =
            response.requests.find((row: SentRequestListItem) => this.requestId === row.id) ?? null
          if (this.request) {
            this.loadMarketingObject(this.request.objectId)
          }
        })
    } else if (this.defaultObjectId) {
      this.loadMarketingObject(this.defaultObjectId)
    }
  },
  methods: {
    syncPublicHeaderLogo() {
      if (!this.isPublicViewingAppointmentsPage) {
        return
      }
      const logoUrl = this.marketingAccount?.logoUrl || null
      if (logoUrl && !this.$store.getters['user/getAccessToken']) {
        this.$store.dispatch('user/setLogoUrl', logoUrl)
      }
    },
    loadMarketingObject(objectId: string | number) {
      ObjectApi.getMarketingObject(objectId)
        .then(response => {
          const obj = response as ApplicationFormMarketingObjectViewModel
          this.object = obj
          const promises: Promise<MediaByIdPayload>[] = []
          for (const media of obj.images) {
            promises.push(MediaApi.getMediaByID(media.mediaId) as Promise<MediaByIdPayload>)
          }
          if (obj.marketingAccountId !== '-1') {
            UserApi.getAccount(obj.marketingAccountId)
              .then(res => {
                this.marketingAccount = res.account as ApplicationFormMarketingAccountDisplay
                this.syncPublicHeaderLogo()
              })
              .catch(e => {
                console.log(e)
                if (!this.isPublicViewingAppointmentsPage) {
                  Vue.toasted.show(this.$t('message.loadingErrors.user'), { type: 'error' })
                }
              })
          }
          Promise.all(promises)
            .then(values => {
              for (const media of values) {
                const order = obj.images.find(image => image.mediaId === media.media.id)?.order ?? 0
                this.galleryImages.push({
                  url: media.media.url,
                  mediaId: media.media.id,
                  order
                })
              }
              this.galleryImages = this.galleryImages.sort((a, b) => a.order - b.order)
            })
            .catch(e => {
              console.log(e)
              Vue.toasted.show(this.$t('message.loadingErrors.image'), { type: 'error' })
            })
            .then(() => {
              this.loading = false
            })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
        })
    },
    openGallery() {
      const media = this.galleryImages.map(image => image.url)
      ;(this as Vue & { $viewerApi: (opts: { images: string[]; options: unknown }) => void }).$viewerApi({
        images: media,
        options: (this as Vue & { viewerOptions: unknown }).viewerOptions
      })
    },
    sumAdditionalAndHeating(additional: number, heating: number): number | '-' {
      const sum =
        (additional !== -1 ? additional : 0) + (heating !== -1 ? heating : 0)
      return sum !== 0 ? sum : '-'
    },
    requestClick(requestId: string | number | '') {
      if (requestId) {
        this.$router.push({ name: 'UserRequestDetailView', params: { id: String(requestId) } })
      }
    },
    applyClicked() {
      this.$router.push({
        name: 'ApplicationFormView',
        query: {
          requestId: String(this.requestId)
        }
      })
    }
  }
})
</script>
<style lang="scss" scoped>
.image-container {
  background-size: cover;
  cursor: pointer;
  min-height: 200px;
  background-position: center;
}

.marketing-account-brand {
  text-align: left;
}

.marketing-account-logo {
  display: block;
  max-height: 50px;
  max-width: 250px;
  width: auto;
  height: auto;
  margin: 0 0 0.5rem;
  object-fit: contain;
  object-position: left center;
}
</style>
